import { CampoBusca, Icone, MenuSuspenso, type ItemMenuSuspenso } from '@persona/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Clock, House, Settings, Star, Stethoscope } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { api } from '../src/api/cliente';
import { semAcento } from '../src/navegacao/busca';
import { useMenu, type ItemMenu } from '../src/navegacao/indice';
import { usePreCarregar } from '../src/navegacao/preCarregar';
import { useSessao } from '../src/sessao/sessao';
import { Marca } from './Marca';
import type { ResumoInicio } from './TelaInicial';

function filtrar(itens: ItemMenu[], termo: string): ItemMenu[] {
  if (!termo) return itens;
  return itens.flatMap((i) => {
    if (i.tipo === 'rotina') return semAcento(`${i.codigo} ${i.nome}`).includes(termo) ? [i] : [];
    if (i.tipo === 'separador') return [];
    const filhos = filtrar(i.itens, termo);
    return filhos.length ? [{ ...i, itens: filhos }] : [];
  });
}

/** Ícone da linha: o do dicionário ou um marcador neutro (o mesmo ícone genérico repetido em toda linha vira ruído). */
function IconeItem({ nome }: { nome?: string | null }) {
  return <span className="trilho-icone" data-vazio={nome ? undefined : true}>{nome && <Icone nome={nome} size={17} />}</span>;
}

interface PropsSecao {
  titulo: string;
  fechada: boolean;
  aoAlternar: () => void;
  children: ReactNode;
}

/** Seção da lateral: título em versalete que recolhe a seção (07 §2.4: o grupo continua um botão com aria-expanded). */
function Secao({ titulo, fechada, aoAlternar, children }: PropsSecao) {
  return (
    <li className="trilho-grupo">
      <button type="button" className="trilho-grupo-botao" aria-expanded={!fechada} onClick={aoAlternar}>
        <span>{titulo}</span>
        <ChevronRight size={12} className="trilho-seta" aria-hidden="true" />
      </button>
      {!fechada && children}
    </li>
  );
}

/**
 * Barra lateral (07 §2.4, ADR-0006): marca, seletor de módulo e busca no topo; "Principal" com início, favoritos e recentes;
 * os grupos do módulo como seções abertas (submenu vira subtítulo, sem terceiro nível); o usuário no rodapé.
 * Sem módulo (tela inicial), a lateral lista os módulos.
 */
export function Trilho({ modulo, recolhido }: { modulo: string | undefined; recolhido: boolean }) {
  const menu = useMenu(modulo);
  const preCarregar = usePreCarregar();
  const navegar = useNavigate();
  const { caminho, rotinaAtiva } = useRouterState({
    select: (s) => ({ caminho: s.location.pathname, rotinaAtiva: /^\/r\/([A-Z0-9]+)/.exec(s.location.pathname)?.[1] }),
  });
  const resumo = useQuery({ queryKey: ['inicio'], queryFn: () => api.get<ResumoInicio>('/inicio/resumo') });
  const { sessao, empresa, estadoLicenca } = useSessao();
  const [fechadas, setFechadas] = useState<ReadonlySet<string>>(() => new Set(['favoritos', 'recentes']));
  const [filtro, setFiltro] = useState('');
  const termo = semAcento(filtro.trim());
  const grupos = useMemo(() => filtrar(menu.data?.grupos ?? [], termo), [menu.data, termo]);
  const modulos = (resumo.data?.modulos ?? []).filter((m) => !termo || semAcento(`${m.codigo} ${m.nome}`).includes(termo));
  const alternar = (id: string) => setFechadas((f) => {
    const n = new Set(f);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  const fechada = (id: string) => !termo && fechadas.has(id);

  const trocarModulo: ItemMenuSuspenso[] = [
    { id: 'inicio', rotulo: 'Início', icone: <House size={15} strokeWidth={1.75} />, aoEscolher: () => void navegar({ to: '/' }) },
    { tipo: 'separador', id: 's' },
    ...(resumo.data?.modulos ?? []).map((m) => ({
      tipo: 'opcao' as const, id: m.codigo, rotulo: `${m.nome} · ${m.codigo}`, marcado: m.codigo.toUpperCase() === modulo,
      aoEscolher: () => void navegar({ to: '/m/$modulo', params: { modulo: m.codigo.toLowerCase() } }),
    })),
  ];

  const rotina = (codigo: string, nome: string, icone?: string | null) => (
    <li key={codigo}>
      <Link to="/r/$rotina" params={{ rotina: codigo }} className="trilho-rotina" data-ativa={codigo === rotinaAtiva || undefined} {...preCarregar(codigo)}
        aria-current={codigo === rotinaAtiva ? 'page' : undefined} title={`${nome} · ${codigo}`}>
        <IconeItem nome={icone} />
        <span className="trilho-rotina-nome">{nome}</span>
        <span className="mono trilho-rotina-codigo">{codigo}</span>
      </Link>
    </li>
  );

  const itensDoGrupo = (itens: ItemMenu[]): ReactNode => (
    <ul className="trilho-lista">
      {itens.map((i, n) => {
        if (i.tipo === 'separador') return <li key={`sep${n}`} className="trilho-separador" role="separator" />;
        if (i.tipo === 'rotina' && i.codigo) return rotina(i.codigo, i.nome, i.icone);
        return (
          <li key={i.codigo ?? i.nome} className="trilho-submenu">
            <span className="trilho-subtitulo">{i.nome}</span>
            {itensDoGrupo(i.itens)}
          </li>
        );
      })}
    </ul>
  );

  const favoritos = resumo.data?.favoritos ?? [];
  const recentes = (resumo.data?.recentes ?? []).slice(0, 8);
  const iniciais = (sessao?.usuario.nome ?? '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <nav className="trilho" aria-label="Menu do módulo" data-recolhido={recolhido || undefined}>
      <div className="trilho-topo">
        <Link to="/" className="trilho-marca" aria-label="Início — Persona ERP"><Marca compacta={recolhido} /></Link>
        {modulo && (
          <MenuSuspenso
            rotulo="Trocar de módulo"
            itens={trocarModulo}
            gatilho={
              <button type="button" className="trilho-modulo" aria-label={`Módulo ${menu.data?.modulo.nome ?? modulo}. Trocar de módulo`}>
                <Icone nome={menu.data?.modulo.icone} size={18} />
                <span className="trilho-modulo-nome">{menu.data?.modulo.nome ?? modulo}</span>
                <ChevronDown size={15} className="trilho-modulo-seta" aria-hidden="true" />
              </button>
            }
          />
        )}
        {!recolhido && (
          <CampoBusca className="trilho-filtro" rotulo={modulo ? 'Buscar rotina no menu' : 'Buscar módulo'} placeholder={modulo ? 'Buscar rotina' : 'Buscar módulo'}
            valor={filtro} aoMudar={setFiltro} />
        )}
      </div>
      <div className="trilho-corpo">
        <ul className="trilho-lista trilho-raiz">
          {!termo && (
            <li className="trilho-grupo">
              <span className="trilho-grupo-titulo">Principal</span>
              <ul className="trilho-lista">
                <li>
                  <Link to="/" className="trilho-rotina" data-ativa={caminho === '/' || undefined} aria-current={caminho === '/' ? 'page' : undefined}>
                    <span className="trilho-icone"><House size={17} strokeWidth={1.75} aria-hidden="true" /></span>
                    <span className="trilho-rotina-nome">Início</span>
                  </Link>
                </li>
                <li>
                  <button type="button" className="trilho-rotina" aria-expanded={!fechada('favoritos')} onClick={() => alternar('favoritos')}>
                    <span className="trilho-icone"><Star size={17} strokeWidth={1.75} aria-hidden="true" /></span>
                    <span className="trilho-rotina-nome">Favoritos</span>
                    <span className="trilho-contagem">{favoritos.length}</span>
                  </button>
                  {!fechada('favoritos') && (
                    <ul className="trilho-lista trilho-aninhada">
                      {favoritos.length ? favoritos.map((f) => rotina(f.codigo, f.nome, f.icone)) : <li className="trilho-vazio">Marque com ★ ou Ctrl+D</li>}
                    </ul>
                  )}
                </li>
                <li>
                  <button type="button" className="trilho-rotina" aria-expanded={!fechada('recentes')} onClick={() => alternar('recentes')}>
                    <span className="trilho-icone"><Clock size={17} strokeWidth={1.75} aria-hidden="true" /></span>
                    <span className="trilho-rotina-nome">Recentes</span>
                  </button>
                  {!fechada('recentes') && (
                    <ul className="trilho-lista trilho-aninhada">
                      {recentes.length ? recentes.map((r) => rotina(r.codigo, r.nome, r.icone)) : <li className="trilho-vazio">Nenhuma rotina aberta ainda</li>}
                    </ul>
                  )}
                </li>
              </ul>
            </li>
          )}
          {modulo
            ? menu.isLoading
              ? <li><div className="esqueleto" style={{ height: 120, margin: 12 }} /></li>
              : grupos.map((g) => (
                <Secao key={g.codigo ?? g.nome} titulo={g.nome} fechada={fechada(g.codigo ?? g.nome)} aoAlternar={() => alternar(g.codigo ?? g.nome)}>
                  {itensDoGrupo(g.itens)}
                </Secao>
              ))
            : (
              <li className="trilho-grupo">
                <span className="trilho-grupo-titulo">Módulos</span>
                <ul className="trilho-lista">
                  {modulos.map((m) => (
                    <li key={m.codigo}>
                      <Link to="/m/$modulo" params={{ modulo: m.codigo.toLowerCase() }} className="trilho-rotina" title={`${m.nome} · ${m.codigo}`}>
                        <IconeItem nome={m.icone} />
                        <span className="trilho-rotina-nome">{m.nome}</span>
                        <span className="mono trilho-rotina-codigo">{m.codigo}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            )}
        </ul>
      </div>
      <div className="trilho-rodape">
        <span className="trilho-avatar" aria-hidden="true">{iniciais}</span>
        <span className="trilho-usuario">
          <span className="trilho-usuario-nome">{sessao?.usuario.nome}</span>
          <span className="trilho-usuario-empresa">{empresa?.nome}</span>
        </span>
        {estadoLicenca !== 'liberado' && <span className="pilula" data-cor={estadoLicenca === 'somente_leitura' || estadoLicenca === 'bloqueado' ? 'erro' : 'aviso'}>Licença</span>}
        <Link to={rotinaAtiva ? '/r/$rotina/diagnostico' : '/diagnostico'} params={rotinaAtiva ? { rotina: rotinaAtiva } : undefined} className="trilho-rodape-botao"
          aria-label="Diagnóstico" title="Diagnóstico">
          <Stethoscope size={16} strokeWidth={1.75} aria-hidden="true" />
        </Link>
        <Link to="/conta" className="trilho-rodape-botao" aria-label="Minha conta" title="Minha conta">
          <Settings size={16} strokeWidth={1.75} aria-hidden="true" />
        </Link>
      </div>
    </nav>
  );
}
